import { ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import MonitoringIcon from 'shared/components/monitoring/monitoring-icon';
import { MonitoringType } from 'shared/components/monitoring/monitoring-type';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';
import { toMonitoringType } from 'shared/enums/fault-type';
import { DeviceType } from 'shared/models/device-type';

import LinkableDevice from 'features/device/linkable-device/domain/models/linkable-device';
import { useDeviceLinkContextProvider } from 'features/device/providers/device-link-provider/device-link-provider';
import DeviceFilters from 'features/device/views/device-filters';
import DeviceInfoModal from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/device-info-modal/device-info-modal';
import useLinkableTransmittersTable from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/linkable-transmitters-table/hooks/use-linkable-transmitters-table';
import styles from './linkable-transmitters-table.module.scss';

type Props = {
  getErrorMessageForDevice?: (device: LinkableDevice) => string | undefined;
  handleOnResetFilters: () => void;
  customFirstRow?: React.ReactNode;
  disabled?: boolean;
  deviceIdToSelect?: string;
  isLinkableTransmitterStepsVisible?: boolean;
};

const LinkableTransmittersTable = ({
  getErrorMessageForDevice,
  handleOnResetFilters,
  customFirstRow,
  disabled,
  deviceIdToSelect,
  isLinkableTransmitterStepsVisible,
}: Props): ReactElement => {
  const { t } = useTranslation('linkableDevice');
  const { t: tDeviceTypes } = useTranslation('deviceTypes');
  const { organisationUnitId, devicesToHide, selectedDevices, handleSelectedDevicesChanged } = useDeviceLinkContextProvider();

  const renderIcon = (device: LinkableDevice): ReactElement => {
    const monitoringType: MonitoringType | undefined = toMonitoringType(device.fault);

    return monitoringType ? <MonitoringIcon type={monitoringType} withTooltip visible size={16} /> : <></>;
  };

  const {
    readLinkableDevicesData,
    readLinkableDevicesIsLoading,
    currentPage,
    currentRowsPerPage,
    currentFilters,
    search,
    sort,
    getFilterValues,
    handleOnPageChanged,
    handleOnRowsPerPageChanged,
    handleOnSortChanged,
    onSearchChanged,
    handleOnOptionsSelected,
    clearFilters,
    setSearch,
  } = useLinkableTransmittersTable({ organisationUnitId, devicesToHide, deviceIdToSelect });

  useEffect(() => {
    if (isLinkableTransmitterStepsVisible) {
      clearFilters();
      setSearch('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLinkableTransmitterStepsVisible]);

  const columns: ColumnDefinition<LinkableDevice, NestedKeyof<LinkableDevice>>[] = [
    {
      key: 'name',
      label: t('linkableDevicesTable.column.name'),
    },
    {
      key: 'type',
      label: t('linkableDevicesTable.column.type'),
      renderCustomContentProvider: (item) => (
        <div className={styles.deviceTypeContainer}>
          <Typography variant="subtitle1">{item.displayType ?? tDeviceTypes(item.type)}</Typography>
          {[DeviceType.G2, DeviceType.G2Transmitter, DeviceType.DesoResidentTransmitter, DeviceType.DesoClient].includes(item.type) && (
            <DeviceInfoModal type={item.type} displayType={item.displayType} />
          )}
        </div>
      ),
      disableSort: true,
    },
    {
      key: 'fault',
      label: '',
      renderCustomContentProvider: renderIcon,
      disableSort: true,
      tableCellProps: {
        align: 'left',
        width: '40px',
      },
    },
  ];

  const hiddenAndLiveDataMergedList =
    readLinkableDevicesData?.devices?.filter((device) => !devicesToHide.some((deviceToHide) => deviceToHide.id === device.id)) ?? [];

  const selectedRows = hiddenAndLiveDataMergedList
    .filter((device) => selectedDevices.some((selected) => selected.id === device.id))
    .map<RowItem<LinkableDevice>>((device) => ({ data: device }));

  return (
    <>
      <DeviceFilters
        onSearchValueChanged={(value: string) => {
          onSearchChanged(value);
          setSearch(value);
        }}
        activeFilters={currentFilters}
        filterValues={getFilterValues()}
        monitoringValues={[]}
        activeMonitoringFilters={[]}
        onOptionsSelected={(filterValueType, keys) => {
          handleOnResetFilters();
          handleOnOptionsSelected(filterValueType, keys);
        }}
        onMonitoringFilterSelected={() => {}}
        clearFilters={() => {
          clearFilters();
          handleOnResetFilters();
        }}
        searchBarValue={search}
        disabled={disabled}
      />
      {readLinkableDevicesIsLoading && <LoadingIndicator />}
      <Table
        className={styles.linkableTransmittersTable}
        data={hiddenAndLiveDataMergedList.map<RowItem<LinkableDevice>>((linkableDevice: LinkableDevice) => ({ data: linkableDevice }))}
        columns={columns}
        rowsAreSelectable
        selectedRows={selectedRows}
        onSelectedRowsChanged={(devices: LinkableDevice[]) => {
          handleSelectedDevicesChanged(devices);
        }}
        pagination={{
          count: readLinkableDevicesData?.total ?? 0,
          page: currentPage,
          rowsPerPage: currentRowsPerPage,
          onPageChanged: handleOnPageChanged,
          onRowsPerPageChanged: handleOnRowsPerPageChanged,
        }}
        onSortChanged={handleOnSortChanged}
        rowIdentifier={(item: LinkableDevice) => item.id}
        initialOrderBy={sort.property}
        initialOrderDirection={sort.isAscending ? 'asc' : 'desc'}
        getErrorMessageForRow={getErrorMessageForDevice}
        customFirstRow={customFirstRow}
        disabled={disabled}
      />
    </>
  );
};

export default LinkableTransmittersTable;
